import React, { useEffect, useMemo, useState } from "react";
import { API, graphqlOperation } from "aws-amplify";
import AppConfirmDialog from "../components/AppConfirmDialog";
import AppDialog from "../components/AppDialog";
import { formatApiError } from "../lib/formatApiError";

interface Organization { id: string; name: string; }
interface Season { id: string; name: string; year: number; }
interface Program { id: string; name: string; type: string; organizationId: string; seasonId?: string | null; }
interface DraftBoard { id: string; name: string; status: string; ageGroup?: string | null; organizationId: string; seasonId: string; programId?: string | null; }

interface EvaluationSession {
  id: string;
  name: string;
  status: string;
  ageGroup?: string | null;
  notes?: string | null;
  organizationId: string;
  seasonId: string;
  programId?: string | null;
  draftBoardId?: string | null;
}

interface EvaluationPlayer {
  id: string;
  evaluationSessionId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  town?: string | null;
  state?: string | null;
  parentEmail?: string | null;
  batting?: number | null;
  fielding?: number | null;
  throwing?: number | null;
  baserunning?: number | null;
  focus?: number | null;
  homeToFirstSeconds?: number | null;
  firstToSecondSeconds?: number | null;
  catcher?: boolean | null;
  pitcher?: boolean | null;
  drafteeId?: string | null;
}

interface Draftee {
  id: string;
  draftBoardId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  parentEmail?: string | null;
}

interface StopwatchState {
  field: "homeToFirstSeconds" | "firstToSecondSeconds";
  label: string;
  startedAt: number | null;
  elapsedMs: number;
  running: boolean;
}

type EvaluationBoardSortKey =
  | "player"
  | "batting"
  | "fielding"
  | "throwing"
  | "baserunning"
  | "focus"
  | "homeToFirstSeconds"
  | "firstToSecondSeconds"
  | "average";

const listEvaluationSessionsQuery = /* GraphQL */ `
  query ListEvaluationSessionsForPage($limit: Int, $nextToken: String) {
    listEvaluationSessions(limit: $limit, nextToken: $nextToken) {
      items { id name status ageGroup notes organizationId seasonId programId draftBoardId }
      nextToken
    }
  }
`;

const listEvaluationPlayersQuery = /* GraphQL */ `
  query ListEvaluationPlayersForPage($limit: Int, $nextToken: String) {
    listEvaluationPlayers(limit: $limit, nextToken: $nextToken) {
      items {
        id evaluationSessionId firstName lastName dateOfBirth town state parentEmail
        batting fielding throwing baserunning focus homeToFirstSeconds firstToSecondSeconds
        catcher pitcher drafteeId
      }
      nextToken
    }
  }
`;

const listOrganizationsQuery = /* GraphQL */ `
  query ListOrganizationsForEvaluations($limit: Int, $nextToken: String) {
    listOrganizations(limit: $limit, nextToken: $nextToken) { items { id name } nextToken }
  }
`;

const listSeasonsQuery = /* GraphQL */ `
  query ListSeasonsForEvaluations($limit: Int, $nextToken: String) {
    listSeasons(limit: $limit, nextToken: $nextToken) { items { id name year } nextToken }
  }
`;

const listProgramsQuery = /* GraphQL */ `
  query ListProgramsForEvaluations($limit: Int, $nextToken: String) {
    listPrograms(limit: $limit, nextToken: $nextToken) { items { id name type organizationId seasonId } nextToken }
  }
`;

const listDraftBoardsQuery = /* GraphQL */ `
  query ListDraftBoardsForEvaluations($limit: Int, $nextToken: String) {
    listDraftBoards(limit: $limit, nextToken: $nextToken) { items { id name status ageGroup organizationId seasonId programId } nextToken }
  }
`;

const listDrafteesQuery = /* GraphQL */ `
  query ListDrafteesForEvaluations($limit: Int, $nextToken: String) {
    listDraftees(limit: $limit, nextToken: $nextToken) { items { id draftBoardId firstName lastName dateOfBirth parentEmail } nextToken }
  }
`;

const createEvaluationSessionMutation = /* GraphQL */ `
  mutation CreateEvaluationSessionForPage($input: CreateEvaluationSessionInput!) {
    createEvaluationSession(input: $input) { id name status ageGroup notes organizationId seasonId programId draftBoardId }
  }
`;

const updateEvaluationSessionMutation = /* GraphQL */ `
  mutation UpdateEvaluationSessionForPage($input: UpdateEvaluationSessionInput!) {
    updateEvaluationSession(input: $input) { id name status ageGroup notes organizationId seasonId programId draftBoardId }
  }
`;

const deleteEvaluationSessionMutation = /* GraphQL */ `
  mutation DeleteEvaluationSessionForPage($input: DeleteEvaluationSessionInput!) {
    deleteEvaluationSession(input: $input) { id }
  }
`;

const createEvaluationPlayerMutation = /* GraphQL */ `
  mutation CreateEvaluationPlayerForPage($input: CreateEvaluationPlayerInput!) {
    createEvaluationPlayer(input: $input) { id }
  }
`;

const updateEvaluationPlayerMutation = /* GraphQL */ `
  mutation UpdateEvaluationPlayerForPage($input: UpdateEvaluationPlayerInput!) {
    updateEvaluationPlayer(input: $input) { id }
  }
`;

const deleteEvaluationPlayerMutation = /* GraphQL */ `
  mutation DeleteEvaluationPlayerForPage($input: DeleteEvaluationPlayerInput!) {
    deleteEvaluationPlayer(input: $input) { id }
  }
`;

const createDrafteeMutation = /* GraphQL */ `
  mutation CreateDrafteeFromEvaluation($input: CreateDrafteeInput!) {
    createDraftee(input: $input) { id }
  }
`;

const updateDrafteeMutation = /* GraphQL */ `
  mutation UpdateDrafteeFromEvaluation($input: UpdateDrafteeInput!) {
    updateDraftee(input: $input) { id }
  }
`;

const splitCsvLine = (line: string): string[] => {
  const out: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === "," && !inQuotes) {
      out.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  out.push(current.trim());
  return out;
};

const normalizeHeader = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, "");
const coerceBoolean = (value: string) => ["1", "true", "yes", "y"].includes(value.trim().toLowerCase());
const playerName = (player: EvaluationPlayer) => `${player.firstName} ${player.lastName}`.trim();
const formatSeconds = (value?: number | null) => (typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : "");

const normalizeDateForApi = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (slashMatch) {
    const month = slashMatch[1].padStart(2, "0");
    const day = slashMatch[2].padStart(2, "0");
    const rawYear = slashMatch[3];
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month}-${day}`;
  }
  const excelSerial = Number(trimmed);
  if (Number.isFinite(excelSerial) && excelSerial > 20000 && excelSerial < 80000) {
    const epoch = new Date(Date.UTC(1899, 11, 30));
    epoch.setUTCDate(epoch.getUTCDate() + excelSerial);
    return epoch.toISOString().slice(0, 10);
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  return null;
};

const nullableScore = (value: string) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(100, parsed)) : null;
};

const computeAverageScore = (player: EvaluationPlayer) => {
  const values = [player.batting, player.fielding, player.throwing, player.baserunning, player.focus].filter(
    (value): value is number => typeof value === "number" && Number.isFinite(value)
  );
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const scoreSnapshot = (player?: Partial<EvaluationPlayer> | null) => ({
  batting: typeof player?.batting === "number" ? player.batting : null,
  fielding: typeof player?.fielding === "number" ? player.fielding : null,
  throwing: typeof player?.throwing === "number" ? player.throwing : null,
  baserunning: typeof player?.baserunning === "number" ? player.baserunning : null,
  focus: typeof player?.focus === "number" ? player.focus : null,
  homeToFirstSeconds: typeof player?.homeToFirstSeconds === "number" ? player.homeToFirstSeconds : null,
  firstToSecondSeconds: typeof player?.firstToSecondSeconds === "number" ? player.firstToSecondSeconds : null,
  catcher: Boolean(player?.catcher),
  pitcher: Boolean(player?.pitcher)
});

const EvaluationsPage: React.FC<{ userRoles: string[] }> = ({ userRoles }) => {
  const canManage = userRoles.includes("SuperAdmin") || userRoles.includes("Commish") || userRoles.includes("Coach");
  const [sessions, setSessions] = useState<EvaluationSession[]>([]);
  const [players, setPlayers] = useState<EvaluationPlayer[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [draftBoards, setDraftBoards] = useState<DraftBoard[]>([]);
  const [draftees, setDraftees] = useState<Draftee[]>([]);
  const [activeSessionId, setActiveSessionId] = useState("");
  const [activePlayerId, setActivePlayerId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncingDraft, setSyncingDraft] = useState(false);
  const [selectedDraftBoardId, setSelectedDraftBoardId] = useState("");
  const [timer, setTimer] = useState<StopwatchState | null>(null);
  const [nowMs, setNowMs] = useState(() => performance.now());
  const [deleteSessionTarget, setDeleteSessionTarget] = useState<EvaluationSession | null>(null);
  const [deletingSession, setDeletingSession] = useState(false);
  const [pendingPlayerId, setPendingPlayerId] = useState("");

  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState("OPEN");
  const [newAgeGroup, setNewAgeGroup] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newOrganizationId, setNewOrganizationId] = useState("");
  const [newSeasonId, setNewSeasonId] = useState("");
  const [newProgramId, setNewProgramId] = useState("");
  const [boardFilter, setBoardFilter] = useState("");
  const [boardSortKey, setBoardSortKey] = useState<EvaluationBoardSortKey>("player");
  const [boardSortDirection, setBoardSortDirection] = useState<"asc" | "desc">("asc");

  const [draft, setDraft] = useState<Partial<EvaluationPlayer>>({});

  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setErrorMessage("");
      }
      const [sessionRes, playerRes, orgRes, seasonRes, programRes, draftRes, drafteeRes] = await Promise.all([
        API.graphql(graphqlOperation(listEvaluationSessionsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listEvaluationPlayersQuery, { limit: 5000 })),
        API.graphql(graphqlOperation(listOrganizationsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listSeasonsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listProgramsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listDraftBoardsQuery, { limit: 500 })),
        API.graphql(graphqlOperation(listDrafteesQuery, { limit: 5000 }))
      ]);
      setSessions(((sessionRes as any)?.data?.listEvaluationSessions?.items ?? []).filter(Boolean));
      setPlayers(((playerRes as any)?.data?.listEvaluationPlayers?.items ?? []).filter(Boolean));
      setOrganizations(((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setSeasons(((seasonRes as any)?.data?.listSeasons?.items ?? []).filter(Boolean));
      setPrograms(((programRes as any)?.data?.listPrograms?.items ?? []).filter(Boolean));
      setDraftBoards(((draftRes as any)?.data?.listDraftBoards?.items ?? []).filter(Boolean));
      setDraftees(((drafteeRes as any)?.data?.listDraftees?.items ?? []).filter(Boolean));
    } catch (error) {
      if (!silent) {
        setErrorMessage(`Failed to load evaluations: ${formatApiError(error)}`);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!activeSessionId && sessions.length > 0) {
      setActiveSessionId(sessions[0].id);
      return;
    }
    if (activeSessionId && !sessions.some((session) => session.id === activeSessionId)) {
      setActiveSessionId(sessions[0]?.id ?? "");
    }
  }, [activeSessionId, sessions]);

  const activeSession = sessions.find((session) => session.id === activeSessionId) ?? null;
  const sessionPlayers = useMemo(
    () => players.filter((player) => player.evaluationSessionId === activeSessionId).sort((a, b) => playerName(a).localeCompare(playerName(b))),
    [activeSessionId, players]
  );
  const activePlayer = sessionPlayers.find((player) => player.id === activePlayerId) ?? sessionPlayers[0] ?? null;
  const scoredCount = useMemo(
    () => sessionPlayers.filter((player) => computeAverageScore(player) !== null).length,
    [sessionPlayers]
  );
  const progressPct = sessionPlayers.length > 0 ? Math.round((scoredCount / sessionPlayers.length) * 100) : 0;

  useEffect(() => {
    if (!activePlayer) {
      setActivePlayerId("");
      setDraft({});
      return;
    }
    if (activePlayerId !== activePlayer.id) {
      setActivePlayerId(activePlayer.id);
    }
    setDraft(activePlayer);
  }, [activePlayer, activePlayerId]);

  useEffect(() => {
    if (!timer?.running) {
      return undefined;
    }
    const interval = window.setInterval(() => setNowMs(performance.now()), 50);
    return () => window.clearInterval(interval);
  }, [timer?.running]);

  const organizationNameById = useMemo(() => organizations.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.id]: item.name }), {}), [organizations]);
  const seasonLabelById = useMemo(() => seasons.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.id]: `${item.name} ${item.year}` }), {}), [seasons]);
  const programNameById = useMemo(() => programs.reduce<Record<string, string>>((acc, item) => ({ ...acc, [item.id]: item.name }), {}), [programs]);

  const compatibleDraftBoards = useMemo(() => {
    if (!activeSession) {
      return [];
    }
    return draftBoards.filter((board) => {
      if (board.organizationId !== activeSession.organizationId) {
        return false;
      }
      if (board.seasonId !== activeSession.seasonId) {
        return false;
      }
      if (activeSession.programId && board.programId !== activeSession.programId) {
        return false;
      }
      if (activeSession.ageGroup && board.ageGroup && activeSession.ageGroup.toUpperCase() !== board.ageGroup.toUpperCase()) {
        return false;
      }
      return true;
    });
  }, [activeSession, draftBoards]);

  const evaluationBoardPlayers = useMemo(() => {
    const normalizedFilter = boardFilter.trim().toLowerCase();
    const filtered = normalizedFilter
      ? sessionPlayers.filter((player) => {
          const haystack = [
            playerName(player),
            player.dateOfBirth,
            player.town,
            player.state,
            player.parentEmail,
            player.pitcher ? "pitcher p" : "",
            player.catcher ? "catcher c" : ""
          ].join(" ").toLowerCase();
          return haystack.includes(normalizedFilter);
        })
      : sessionPlayers;

    const valueForSort = (player: EvaluationPlayer): string | number => {
      if (boardSortKey === "player") {
        return playerName(player).toLowerCase();
      }
      if (boardSortKey === "average") {
        return computeAverageScore(player) ?? -1;
      }
      const value = player[boardSortKey];
      return typeof value === "number" && Number.isFinite(value) ? value : -1;
    };

    return [...filtered].sort((a, b) => {
      const left = valueForSort(a);
      const right = valueForSort(b);
      const direction = boardSortDirection === "asc" ? 1 : -1;
      if (typeof left === "string" || typeof right === "string") {
        return String(left).localeCompare(String(right)) * direction;
      }
      return (left - right) * direction;
    });
  }, [boardFilter, boardSortDirection, boardSortKey, sessionPlayers]);

  const toggleBoardSort = (key: EvaluationBoardSortKey) => {
    if (boardSortKey === key) {
      setBoardSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setBoardSortKey(key);
    setBoardSortDirection(key === "player" ? "asc" : "desc");
  };

  const boardSortLabel = (key: EvaluationBoardSortKey) => (boardSortKey === key ? (boardSortDirection === "asc" ? " ^" : " v") : "");

  const hasUnsavedScoreChanges = useMemo(() => {
    if (!activePlayer) {
      return false;
    }
    return JSON.stringify(scoreSnapshot(activePlayer)) !== JSON.stringify(scoreSnapshot(draft));
  }, [activePlayer, draft]);

  const requestPlayerSelection = (playerId: string) => {
    if (!playerId || playerId === activePlayer?.id) {
      return;
    }
    if (hasUnsavedScoreChanges) {
      setPendingPlayerId(playerId);
      return;
    }
    setActivePlayerId(playerId);
  };

  useEffect(() => {
    if (syncOpen && !selectedDraftBoardId) {
      setSelectedDraftBoardId(activeSession?.draftBoardId || compatibleDraftBoards[0]?.id || "");
    }
  }, [activeSession?.draftBoardId, compatibleDraftBoards, selectedDraftBoardId, syncOpen]);

  const createSession = async () => {
    if (!newName.trim() || !newOrganizationId || !newSeasonId) {
      setErrorMessage("Evaluation name, organization, and season are required.");
      return;
    }
    try {
      setErrorMessage("");
      const res: any = await API.graphql(graphqlOperation(createEvaluationSessionMutation, {
        input: {
          name: newName.trim(),
          status: newStatus,
          ageGroup: newAgeGroup.trim() || null,
          notes: newNotes.trim() || null,
          organizationId: newOrganizationId,
          seasonId: newSeasonId,
          programId: newProgramId || null
        }
      }));
      const created = res?.data?.createEvaluationSession as EvaluationSession | undefined;
      setCreateOpen(false);
      setNewName("");
      setNewStatus("OPEN");
      setNewAgeGroup("");
      setNewNotes("");
      setNewOrganizationId("");
      setNewSeasonId("");
      setNewProgramId("");
      await loadData(true);
      if (created?.id) {
        setActiveSessionId(created.id);
      }
    } catch (error) {
      setErrorMessage(`Failed to create evaluation: ${formatApiError(error)}`);
    }
  };

  const deleteSession = async (session: EvaluationSession) => {
    setDeletingSession(true);
    try {
      setErrorMessage("");
      for (const player of players.filter((item) => item.evaluationSessionId === session.id)) {
        await API.graphql(graphqlOperation(deleteEvaluationPlayerMutation, { input: { id: player.id } }));
      }
      await API.graphql(graphqlOperation(deleteEvaluationSessionMutation, { input: { id: session.id } }));
      setDeleteSessionTarget(null);
      await loadData(true);
    } catch (error) {
      setErrorMessage(`Failed to delete evaluation: ${formatApiError(error)}`);
    } finally {
      setDeletingSession(false);
    }
  };

  const uploadRoster = async (file: File) => {
    if (!activeSession) {
      setErrorMessage("Create or select an evaluation before uploading players.");
      return;
    }
    try {
      setErrorMessage("");
      setStatusMessage("");
      const raw = await file.text();
      const lines = raw.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      if (lines.length < 2) {
        throw new Error("CSV must include a header row and at least one player.");
      }
      const headers = splitCsvLine(lines[0]).map(normalizeHeader);
      const headerMap: Record<string, number> = {};
      headers.forEach((header, index) => {
        headerMap[header] = index;
      });
      const getCell = (cells: string[], aliases: string[]) => {
        for (const alias of aliases) {
          const idx = headerMap[alias];
          if (typeof idx === "number") {
            return (cells[idx] ?? "").trim();
          }
        }
        return "";
      };
      const missing = ["firstname", "lastname", "dateofbirth", "town", "state", "parentemail"].filter((field) => typeof headerMap[field] !== "number");
      if (missing.length > 0) {
        throw new Error(`CSV is missing required columns: ${missing.join(", ")}.`);
      }

      const existingKeys = new Set(
        sessionPlayers.map((player) => `${player.firstName}|${player.lastName}|${player.dateOfBirth}|${(player.parentEmail ?? "").toLowerCase()}`.toLowerCase())
      );
      let createdCount = 0;
      const rowErrors: string[] = [];
      for (let index = 1; index < lines.length; index += 1) {
        const cells = splitCsvLine(lines[index]);
        const rowNumber = index + 1;
        const firstName = getCell(cells, ["firstname", "first"]);
        const lastName = getCell(cells, ["lastname", "last"]);
        const rawDateOfBirth = getCell(cells, ["dateofbirth", "dob", "birthdate"]);
        const dateOfBirth = normalizeDateForApi(rawDateOfBirth);
        const town = getCell(cells, ["town", "city"]);
        const state = getCell(cells, ["state"]);
        const parentEmail = getCell(cells, ["parentemail", "email"]);
        if (!firstName || !lastName || !rawDateOfBirth || !town || !state || !parentEmail) {
          rowErrors.push(`Row ${rowNumber}: missing one or more required values.`);
          continue;
        }
        if (!dateOfBirth) {
          rowErrors.push(`Row ${rowNumber}: invalid date of birth "${rawDateOfBirth}".`);
          continue;
        }
        const key = `${firstName}|${lastName}|${dateOfBirth}|${parentEmail.toLowerCase()}`.toLowerCase();
        if (existingKeys.has(key)) {
          rowErrors.push(`Row ${rowNumber}: ${firstName} ${lastName} is already in this evaluation.`);
          continue;
        }
        await API.graphql(graphqlOperation(createEvaluationPlayerMutation, {
          input: {
            evaluationSessionId: activeSession.id,
            firstName,
            lastName,
            dateOfBirth,
            town,
            state: state.toUpperCase(),
            parentEmail: parentEmail.toLowerCase(),
            catcher: coerceBoolean(getCell(cells, ["catcher"])),
            pitcher: coerceBoolean(getCell(cells, ["pitcher"]))
          }
        }));
        existingKeys.add(key);
        createdCount += 1;
      }
      await loadData(true);
      setUploadOpen(false);
      setStatusMessage(`Roster upload complete. Added ${createdCount} player${createdCount === 1 ? "" : "s"}${rowErrors.length ? `, ${rowErrors.length} skipped` : ""}.`);
      if (rowErrors.length > 0) {
        setErrorMessage(rowErrors.slice(0, 8).join(" | "));
      }
    } catch (error) {
      setErrorMessage(`Roster upload failed: ${formatApiError(error)}`);
    }
  };

  const saveActivePlayer = async (options: { silent?: boolean } = {}) => {
    if (!activePlayer) {
      return false;
    }
    setSavingPlayer(true);
    setErrorMessage("");
    if (!options.silent) {
      setStatusMessage("");
    }
    try {
      const snapshot = scoreSnapshot(draft);
      await API.graphql(graphqlOperation(updateEvaluationPlayerMutation, {
        input: {
          id: activePlayer.id,
          ...snapshot
        }
      }));
      await loadData(true);
      if (!options.silent) {
        setStatusMessage(`${playerName(activePlayer)} saved.`);
      }
      return true;
    } catch (error) {
      setErrorMessage(`Failed to save score: ${formatApiError(error)}`);
      return false;
    } finally {
      setSavingPlayer(false);
    }
  };

  const saveAndGoToNextPlayer = async () => {
    if (!activePlayer) {
      return;
    }
    const currentIndex = sessionPlayers.findIndex((player) => player.id === activePlayer.id);
    const next = sessionPlayers[(currentIndex + 1) % sessionPlayers.length];
    const saved = await saveActivePlayer({ silent: true });
    if (saved && next) {
      setActivePlayerId(next.id);
      setStatusMessage(`${playerName(activePlayer)} saved. Moved to ${playerName(next)}.`);
    }
  };

  const syncToDraft = async () => {
    if (!activeSession || !selectedDraftBoardId) {
      setErrorMessage("Select a draft board before sending evaluations.");
      return;
    }
    setSyncingDraft(true);
    setErrorMessage("");
    setStatusMessage("");
    try {
      const existing = draftees.filter((draftee) => draftee.draftBoardId === selectedDraftBoardId);
      let createdCount = 0;
      let updatedCount = 0;
      for (const player of sessionPlayers) {
        const existingDraftee = existing.find((draftee) =>
          draftee.firstName.trim().toLowerCase() === player.firstName.trim().toLowerCase() &&
          draftee.lastName.trim().toLowerCase() === player.lastName.trim().toLowerCase() &&
          draftee.dateOfBirth === player.dateOfBirth &&
          (draftee.parentEmail ?? "").trim().toLowerCase() === (player.parentEmail ?? "").trim().toLowerCase()
        );
        const input = {
          draftBoardId: selectedDraftBoardId,
          firstName: player.firstName,
          lastName: player.lastName,
          dateOfBirth: player.dateOfBirth,
          town: player.town || null,
          state: player.state || null,
          parentEmail: player.parentEmail || null,
          batting: player.batting ?? null,
          fielding: player.fielding ?? null,
          throwing: player.throwing ?? null,
          baserunning: player.baserunning ?? null,
          coachability: player.focus ?? null,
          catcher: Boolean(player.catcher),
          pitcher: Boolean(player.pitcher)
        };
        if (existingDraftee) {
          await API.graphql(graphqlOperation(updateDrafteeMutation, { input: { id: existingDraftee.id, ...input } }));
          await API.graphql(graphqlOperation(updateEvaluationPlayerMutation, { input: { id: player.id, drafteeId: existingDraftee.id } }));
          updatedCount += 1;
        } else {
          const created: any = await API.graphql(graphqlOperation(createDrafteeMutation, { input }));
          const drafteeId = created?.data?.createDraftee?.id;
          if (drafteeId) {
            await API.graphql(graphqlOperation(updateEvaluationPlayerMutation, { input: { id: player.id, drafteeId } }));
          }
          createdCount += 1;
        }
      }
      await API.graphql(graphqlOperation(updateEvaluationSessionMutation, { input: { id: activeSession.id, draftBoardId: selectedDraftBoardId, status: "SENT_TO_DRAFT" } }));
      await loadData(true);
      setSyncOpen(false);
      setStatusMessage(`Sent evaluations to draft. Created ${createdCount}, updated ${updatedCount}.`);
    } catch (error) {
      setErrorMessage(`Failed to send evaluations to draft: ${formatApiError(error)}`);
    } finally {
      setSyncingDraft(false);
    }
  };

  const setScore = (field: keyof EvaluationPlayer, value: string) => {
    setDraft((current) => ({ ...current, [field]: nullableScore(value) }));
  };

  const openTimer = (field: StopwatchState["field"], label: string) => {
    const existingSeconds = Number(draft[field] ?? 0);
    setNowMs(performance.now());
    setTimer({
      field,
      label,
      startedAt: null,
      elapsedMs: Number.isFinite(existingSeconds) ? existingSeconds * 1000 : 0,
      running: false
    });
  };

  const elapsedMs = timer ? timer.elapsedMs + (timer.running && timer.startedAt ? nowMs - timer.startedAt : 0) : 0;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Evaluations</h1>
      <p className="text-gray-600 mb-6">Score tryout players and send finished ratings into the draft board.</p>

      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {statusMessage && <p className="mb-4" style={{ color: "#166534" }}>{statusMessage}</p>}

      <div className="bubble-card p-4 mb-4 flex gap-2 flex-wrap">
        <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={() => setCreateOpen(true)}>
          New Evaluation
        </button>
        <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={() => setUploadOpen(true)} disabled={!activeSession}>
          Upload Roster CSV
        </button>
        <button className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600" onClick={() => setSyncOpen(true)} disabled={!activeSession || sessionPlayers.length === 0}>
          Send To Draft
        </button>
        <button className="bg-slate-200 text-slate-900 px-4 py-2 rounded hover:bg-slate-300" onClick={() => loadData()}>
          Refresh
        </button>
      </div>

      <div className="evaluation-workspace">
        <div className="bubble-card p-4">
          <h2 className="text-lg font-semibold mb-3">Sessions</h2>
          <div className="grid gap-2">
            {sessions.map((session) => (
              <button
                key={session.id}
                className={`lineup-plan-card ${session.id === activeSessionId ? "lineup-plan-card--active" : ""}`}
                onClick={() => setActiveSessionId(session.id)}
              >
                <div className="lineup-plan-card__title">{session.name}</div>
                <div className="lineup-plan-card__meta">
                  {organizationNameById[session.organizationId] ?? "Organization"} | {seasonLabelById[session.seasonId] ?? "Season"} | {session.status}
                </div>
              </button>
            ))}
            {sessions.length === 0 && <p className="text-gray-600">No evaluations yet.</p>}
          </div>
          {activeSession ? (
            <div className="mt-4 text-sm text-gray-700">
              <div><strong>Players:</strong> {sessionPlayers.length}</div>
              <div><strong>Program:</strong> {programNameById[activeSession.programId ?? ""] ?? "None"}</div>
              <div><strong>Age:</strong> {activeSession.ageGroup || "Any"}</div>
              {canManage && (
                <button className="bg-red-600 text-white px-3 py-1 rounded mt-3" onClick={() => setDeleteSessionTarget(activeSession)}>
                  Delete Evaluation
                </button>
              )}
            </div>
          ) : null}
        </div>

        <div className="bubble-card p-4">
          <div className="flex justify-between gap-3 flex-wrap mb-4">
            <div>
              <h2 className="text-lg font-semibold">Mobile Scoring</h2>
              <p className="text-sm text-gray-600">Pick a player, score the fields, and save before moving on.</p>
            </div>
            <div className="bubble-pill-wrap">
              <span className="bubble-pill">{scoredCount} scored</span>
              <span className="bubble-pill">{sessionPlayers.length} total</span>
            </div>
          </div>
          {sessionPlayers.length > 0 && (
            <div className="progress-track mb-4" aria-label={`Evaluation ${progressPct}% complete`}>
              <div className="progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          )}

          {!activeSession ? (
            <p className="text-gray-600">Create or select an evaluation to begin.</p>
          ) : sessionPlayers.length === 0 ? (
            <p className="text-gray-600">Upload a roster CSV to add players to this evaluation.</p>
          ) : (
            <>
              <select value={activePlayer?.id ?? ""} onChange={(event) => requestPlayerSelection(event.target.value)} className="border px-3 py-2 rounded w-full mb-3 text-base">
                {sessionPlayers.map((player) => (
                  <option key={player.id} value={player.id}>
                    {playerName(player)} {computeAverageScore(player) !== null ? "- scored" : ""}
                  </option>
                ))}
              </select>

              {activePlayer ? (
                <div>
                  <div className="mb-4">
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <h3 className="text-xl font-bold">{playerName(activePlayer)}</h3>
                      {hasUnsavedScoreChanges ? (
                        <span className="bubble-pill" style={{ background: "#fff7ed", color: "#9a3412", borderColor: "#fed7aa" }}>
                          Unsaved changes
                        </span>
                      ) : (
                        <span className="bubble-pill" style={{ background: "#ecfdf3", color: "#166534", borderColor: "#bbf7d0" }}>
                          Saved
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      DOB {activePlayer.dateOfBirth} | {activePlayer.town ?? "-"}, {activePlayer.state ?? "-"}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {(["batting", "fielding", "throwing", "baserunning", "focus"] as const).map((field) => (
                      <label key={field} className="lineup-planner__field">
                        <span>{field === "focus" ? "Focus" : field.charAt(0).toUpperCase() + field.slice(1)}</span>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={(draft[field] as number | null | undefined) ?? ""}
                          onChange={(event) => setScore(field, event.target.value)}
                          className="border px-3 py-2 rounded text-base"
                        />
                      </label>
                    ))}
                    <label className="lineup-planner__field">
                      <span>Home to 1st</span>
                      <button type="button" className="border px-3 py-2 rounded text-left bg-white" onClick={() => openTimer("homeToFirstSeconds", "Home to 1st")}>
                        {formatSeconds(draft.homeToFirstSeconds as number | null)} sec
                      </button>
                    </label>
                    <label className="lineup-planner__field">
                      <span>1st to 2nd</span>
                      <button type="button" className="border px-3 py-2 rounded text-left bg-white" onClick={() => openTimer("firstToSecondSeconds", "1st to 2nd")}>
                        {formatSeconds(draft.firstToSecondSeconds as number | null)} sec
                      </button>
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" checked={Boolean(draft.pitcher)} onChange={(event) => setDraft((current) => ({ ...current, pitcher: event.target.checked }))} />
                      Pitcher
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" checked={Boolean(draft.catcher)} onChange={(event) => setDraft((current) => ({ ...current, catcher: event.target.checked }))} />
                      Catcher
                    </label>
                  </div>

                  <div className="flex gap-2 flex-wrap mt-4">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={() => void saveActivePlayer()} disabled={savingPlayer}>
                      {savingPlayer ? "Saving..." : "Save Score"}
                    </button>
                    <button
                      className="bg-slate-200 text-slate-900 px-4 py-2 rounded hover:bg-slate-300"
                      onClick={saveAndGoToNextPlayer}
                      disabled={savingPlayer}
                    >
                      Save & Next
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="bubble-card p-4 mt-4">
        <div className="flex justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-lg font-semibold mb-1">Evaluation Board</h2>
            <p className="text-sm text-gray-600">Sortable scorecard for the current evaluation session.</p>
          </div>
          <input
            className="border px-3 py-2 rounded"
            style={{ minWidth: "min(100%, 280px)" }}
            value={boardFilter}
            onChange={(event) => setBoardFilter(event.target.value)}
            placeholder="Filter player, town, email, P/C..."
          />
        </div>
        <div className="bubble-table-wrapper">
          <table className="bubble-table">
            <thead>
              <tr>
                <th><button className="table-sort" onClick={() => toggleBoardSort("player")}>Player{boardSortLabel("player")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("batting")}>Bat{boardSortLabel("batting")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("fielding")}>Field{boardSortLabel("fielding")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("throwing")}>Throw{boardSortLabel("throwing")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("baserunning")}>Run{boardSortLabel("baserunning")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("focus")}>Focus{boardSortLabel("focus")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("homeToFirstSeconds")}>H-1{boardSortLabel("homeToFirstSeconds")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("firstToSecondSeconds")}>1-2{boardSortLabel("firstToSecondSeconds")}</button></th>
                <th><button className="table-sort" onClick={() => toggleBoardSort("average")}>Avg{boardSortLabel("average")}</button></th>
              </tr>
            </thead>
            <tbody>
              {sessionPlayers.length === 0 ? (
                <tr><td colSpan={9} className="text-gray-600">No players loaded.</td></tr>
              ) : evaluationBoardPlayers.length === 0 ? (
                <tr><td colSpan={9} className="text-gray-600">No players match that filter.</td></tr>
              ) : evaluationBoardPlayers.map((player) => (
                <tr key={player.id}>
                  <td>
                    <button className="table-sort" onClick={() => requestPlayerSelection(player.id)}>{playerName(player)}</button>
                    <div className="bubble-pill-wrap mt-1">
                      {player.pitcher ? <span className="bubble-pill">P</span> : null}
                      {player.catcher ? <span className="bubble-pill">C</span> : null}
                    </div>
                  </td>
                  <td>{player.batting ?? "-"}</td>
                  <td>{player.fielding ?? "-"}</td>
                  <td>{player.throwing ?? "-"}</td>
                  <td>{player.baserunning ?? "-"}</td>
                  <td>{player.focus ?? "-"}</td>
                  <td>{formatSeconds(player.homeToFirstSeconds)}</td>
                  <td>{formatSeconds(player.firstToSecondSeconds)}</td>
                  <td>{computeAverageScore(player)?.toFixed(1) ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Evaluation"
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={createSession}>Create</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input className="border px-3 py-2 rounded" value={newName} onChange={(event) => setNewName(event.target.value)} placeholder="Evaluation name" />
          <select className="border px-3 py-2 rounded" value={newStatus} onChange={(event) => setNewStatus(event.target.value)}>
            <option value="OPEN">Open</option>
            <option value="COMPLETE">Complete</option>
          </select>
          <select className="border px-3 py-2 rounded" value={newOrganizationId} onChange={(event) => setNewOrganizationId(event.target.value)}>
            <option value="">Organization</option>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
          <select className="border px-3 py-2 rounded" value={newSeasonId} onChange={(event) => setNewSeasonId(event.target.value)}>
            <option value="">Season</option>
            {seasons.map((season) => <option key={season.id} value={season.id}>{season.name} {season.year}</option>)}
          </select>
          <select className="border px-3 py-2 rounded" value={newProgramId} onChange={(event) => setNewProgramId(event.target.value)}>
            <option value="">No program</option>
            {programs
              .filter((program) => (!newOrganizationId || program.organizationId === newOrganizationId) && (!newSeasonId || !program.seasonId || program.seasonId === newSeasonId))
              .map((program) => <option key={program.id} value={program.id}>{program.name}</option>)}
          </select>
          <input className="border px-3 py-2 rounded" value={newAgeGroup} onChange={(event) => setNewAgeGroup(event.target.value)} placeholder="Age group, ex. 11U" />
          <textarea className="border px-3 py-2 rounded md:col-span-2" value={newNotes} onChange={(event) => setNewNotes(event.target.value)} placeholder="Notes" />
        </div>
      </AppDialog>

      <AppDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        title="Upload Evaluation Roster"
        footer={<button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setUploadOpen(false)}>Close</button>}
      >
        <p className="text-sm text-gray-600 mb-3">
          Required columns: First Name, Last Name, Date of Birth, Town, State, Parent Email. Optional: Pitcher, Catcher.
        </p>
        <p className="text-sm mb-3"><a href="/sample-evaluation-roster.csv" download>Download sample CSV</a></p>
        <input
          type="file"
          accept=".csv,text/csv"
          className="border px-3 py-2 rounded w-full"
          onChange={async (event) => {
            const file = event.currentTarget.files?.[0];
            if (file) {
              await uploadRoster(file);
              event.currentTarget.value = "";
            }
          }}
        />
      </AppDialog>

      <AppDialog
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        title="Send Evaluation To Draft"
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setSyncOpen(false)}>Cancel</button>
            <button className="bg-yellow-500 text-white px-4 py-2 rounded" onClick={syncToDraft} disabled={syncingDraft || !selectedDraftBoardId}>
              {syncingDraft ? "Sending..." : "Send"}
            </button>
          </>
        }
      >
        <select value={selectedDraftBoardId} onChange={(event) => setSelectedDraftBoardId(event.target.value)} className="border px-3 py-2 rounded w-full">
          <option value="">Select draft board</option>
          {compatibleDraftBoards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name} | {board.status} | {board.ageGroup || "Any age"}
            </option>
          ))}
        </select>
      </AppDialog>

      <AppDialog
        open={Boolean(timer)}
        onClose={() => setTimer(null)}
        title={timer?.label ?? "Stopwatch"}
        footer={
          <>
            <button className="bg-gray-200 text-gray-800 px-4 py-2 rounded" onClick={() => setTimer(null)}>Cancel</button>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded"
              onClick={() => {
                if (!timer) {
                  return;
                }
                const seconds = Number((elapsedMs / 1000).toFixed(2));
                setDraft((current) => ({ ...current, [timer.field]: seconds }));
                setTimer(null);
              }}
            >
              Use Time
            </button>
          </>
        }
      >
        <div className="text-center">
          <div style={{ fontSize: "3rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{(elapsedMs / 1000).toFixed(2)}</div>
          <div className="flex justify-center gap-2 mt-4">
            <button
              className="bg-green-600 text-white px-4 py-2 rounded"
              onClick={() => setTimer((current) => current ? { ...current, startedAt: performance.now(), running: true } : current)}
              disabled={Boolean(timer?.running)}
            >
              Start
            </button>
            <button
              className="bg-yellow-500 text-white px-4 py-2 rounded"
              onClick={() => setTimer((current) => current ? { ...current, elapsedMs, startedAt: null, running: false } : current)}
              disabled={!timer?.running}
            >
              Stop
            </button>
            <button
              className="bg-slate-200 text-slate-900 px-4 py-2 rounded"
              onClick={() => {
                setNowMs(performance.now());
                setTimer((current) => current ? { ...current, startedAt: null, elapsedMs: 0, running: false } : current);
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </AppDialog>

      <AppConfirmDialog
        open={Boolean(deleteSessionTarget)}
        title="Delete Evaluation"
        destructive
        busy={deletingSession}
        confirmLabel="Delete"
        message={
          <p>
            Delete <strong>{deleteSessionTarget?.name}</strong> and every player loaded into that evaluation? This cannot be undone.
          </p>
        }
        onCancel={() => {
          if (!deletingSession) {
            setDeleteSessionTarget(null);
          }
        }}
        onConfirm={() => {
          if (deleteSessionTarget) {
            void deleteSession(deleteSessionTarget);
          }
        }}
      />

      <AppConfirmDialog
        open={Boolean(pendingPlayerId)}
        title="Discard Unsaved Score?"
        confirmLabel="Switch Player"
        message={
          <p>
            You have unsaved scoring changes for <strong>{activePlayer ? playerName(activePlayer) : "this player"}</strong>.
            Switching players will discard those edits.
          </p>
        }
        onCancel={() => setPendingPlayerId("")}
        onConfirm={() => {
          setActivePlayerId(pendingPlayerId);
          setPendingPlayerId("");
        }}
      />
    </div>
  );
};

export default EvaluationsPage;
